import { Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useState, useRef, type ChangeEvent, type FocusEvent } from 'react';

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

const COMMON_DOMAINS = [
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
  'live.com',
  'protonmail.com',
  'me.com',
];

const COMMON_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.con': 'gmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlook.co': 'outlook.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'icloud.co': 'icloud.com',
  'icloud.con': 'icloud.com',
};

function getEmailSuggestion(email: string): string | null {
  const atIndex = email.indexOf('@');
  if (atIndex < 1) return null;
  const domain = email.slice(atIndex + 1).toLowerCase();
  if (!domain) return null;

  // Check for common typos
  if (COMMON_TYPOS[domain]) {
    return email.slice(0, atIndex + 1) + COMMON_TYPOS[domain];
  }

  // Suggest close matches for partial domains
  if (domain.length >= 3 && !domain.includes('.')) {
    const match = COMMON_DOMAINS.find((d) => d.startsWith(domain));
    if (match) return email.slice(0, atIndex + 1) + match;
  }

  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function validateEmail(email: string): string | null {
  if (!email) return 'Email is required';
  if (!email.includes('@')) return 'Please include an @ in the email address';
  const [local, domain] = email.split('@');
  if (!local) return 'Please enter the part before @';
  if (!domain || !domain.includes('.'))
    return 'Please enter a complete email domain (e.g. gmail.com)';
  if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address';
  return null;
}

interface EmailInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  id?: string;
  label?: string;
  showIcon?: boolean;
  className?: string;
  inputClassName?: string;
  /** Variant controls styling preset */
  variant?: 'auth' | 'form';
  /** Override autoComplete attribute (default: "email") */
  autoComplete?: string;
  /** HTML name attribute — required for useActionState form data */
  name?: string;
}

export function EmailInput({
  value,
  onChange,
  placeholder = 'Enter your email',
  autoFocus = false,
  disabled = false,
  id,
  label,
  showIcon = true,
  className = '',
  inputClassName = '',
  variant = 'auth',
  autoComplete = 'email',
  name,
}: EmailInputProps) {
  const [touched, setTouched] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const error = touched ? validateEmail(value) : null;
  const isValid = touched && !error && value.length > 0;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    setSuggestion(getEmailSuggestion(val));
  };

  const handleBlur = (_e: FocusEvent<HTMLInputElement>) => {
    if (value) setTouched(true);
  };

  const applySuggestion = () => {
    if (suggestion) {
      onChange(suggestion);
      setSuggestion(null);
      setTouched(true);
    }
  };

  const baseInputClass =
    variant === 'auth'
      ? 'w-full py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all'
      : 'input w-full';

  const borderClass = error
    ? 'border-red-500/50'
    : isValid
      ? 'border-green-500/50'
      : 'border-gray-600';

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        {showIcon && (
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        )}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="email"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          required
          autoFocus={autoFocus}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`${baseInputClass} ${borderClass} ${showIcon ? 'pl-11 pr-10' : 'pr-10'} ${inputClassName}`}
        />
        {/* Status icon */}
        {touched && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {error ? (
              <AlertCircle className="w-4 h-4 text-red-400" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-green-400" />
            )}
          </div>
        )}
      </div>

      {/* Validation error */}
      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}

      {/* Domain suggestion */}
      {suggestion && !error && (
        <button
          type="button"
          onClick={applySuggestion}
          className="mt-1.5 text-xs text-primary-400 hover:text-primary-300 transition-colors"
        >
          Did you mean <span className="font-medium underline">{suggestion}</span>?
        </button>
      )}
    </div>
  );
}
